(function () {
  "use strict";

  window.TND = window.TND || {};

  var DRAWER_TAG = "tnd-unified-drawer";
  var OPEN_CLASS = "tnd-drawer-open";
  var REFRESH_DEBOUNCE_MS = 10000;
  var TRANSITION_MS = 240;
  var RECENT_LAUNCH_MS = 2 * 60 * 60 * 1000;
  var NUDGE_KEY = "tnd_webview_nudge_dismissed";
  // CR-5: in-app webviews (no Apple Pay/Shop Pay). Nudge, never block.
  var WEBVIEW_UA_RE = /FBAN|FBAV|Instagram|TikTok|musical_ly|FB_IAB|Snapchat|Pinterest|LinkedInApp/i;

  function inAppWebview() {
    try {
      return WEBVIEW_UA_RE.test(navigator.userAgent || "");
    } catch (error) {
      return false;
    }
  }

  function isAndroid() {
    try {
      return /Android/i.test(navigator.userAgent || "");
    } catch (error) {
      return false;
    }
  }

  function nudgeDismissed() {
    try {
      return sessionStorage.getItem(NUDGE_KEY) === "1";
    } catch (error) {
      return false;
    }
  }

  function getDrawer() {
    return document.querySelector(DRAWER_TAG);
  }

  function htmlEscape(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatCents(cents) {
    var value = Number(cents || 0) / 100;
    return "$" + value.toFixed(2);
  }

  function formatSpiritsMoney(amount, currency) {
    var code = currency || "USD";
    var normalized = Number(amount || 0).toFixed(2);
    return code === "USD" ? "$" + normalized : normalized + " " + code;
  }

  function safeCall(fn, fallback) {
    try {
      return typeof fn === "function" ? fn() : fallback;
    } catch (error) {
      console.warn("[tnd-drawer] source read failed", error);
      return fallback;
    }
  }

  function relativeTime(value) {
    if (!value) return "";
    var then = new Date(value).getTime();
    if (!Number.isFinite(then)) return "";
    var seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (seconds < 60) return " just now";
    var minutes = Math.round(seconds / 60);
    if (minutes < 60) return " " + minutes + "m ago";
    var hours = Math.round(minutes / 60);
    if (hours < 24) return " " + hours + "h ago";
    return " " + Math.round(hours / 24) + "d ago";
  }

  class TndUnifiedDrawer extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._isClosing = false;
      this._pushed = false;
      this._lastFocus = null;
      this._lastRefreshAt = 0;
      this._refreshTimer = 0;
      this._lastSpiritsData = null;
      this._lastTndData = null;
      this._writeBusy = false;
      this._apparelPaid = false;
      this._nudgeDismissed = nudgeDismissed();
      this._interstitialPushed = false;
      this._suppressInterstitialPopstate = false;
      this._boundLaunchRefresh = null;
      this._boundPopstate = this._onPopstate.bind(this);
      this._boundKeydown = this._onKeydown.bind(this);
      this._boundRefreshTrigger = this._onRefreshTrigger.bind(this);
      this._boundAgeVerified = this._onAgeVerified.bind(this);
    }

    connectedCallback() {
      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      this.setAttribute("aria-labelledby", "tnd-drawer-title");
      // A DOM move (theme editor) can reconnect mid-close with _isClosing
      // latched and the popstate that would clear it already missed.
      this._isClosing = false;
      this._renderShell();
      window.addEventListener("popstate", this._boundPopstate);
      window.addEventListener("focus", this._boundRefreshTrigger);
      document.addEventListener("visibilitychange", this._boundRefreshTrigger);
      document.addEventListener("tnd:age-verified", this._boundAgeVerified);
      if (window.TND.launch && typeof window.TND.launch.onLaunch === "function") {
        this._boundLaunchRefresh = window.TND.launch.onLaunch(() => this.refresh());
      }
    }

    disconnectedCallback() {
      window.removeEventListener("popstate", this._boundPopstate);
      window.removeEventListener("focus", this._boundRefreshTrigger);
      document.removeEventListener("visibilitychange", this._boundRefreshTrigger);
      document.removeEventListener("tnd:age-verified", this._boundAgeVerified);
      document.removeEventListener("keydown", this._boundKeydown);
      if (this._boundLaunchRefresh) this._boundLaunchRefresh();
    }

    open(trigger) {
      if (this._isOpen) return;
      this._isOpen = true;
      this._isClosing = false;
      this._lastFocus = trigger || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add(OPEN_CLASS);
      requestAnimationFrame(() => {
        this.setAttribute("data-open", "true");
      });
      document.addEventListener("keydown", this._boundKeydown);
      this.refresh().then(() => this._prewarmSpirits(this._lastSpiritsData));

      // Move focus into the dialog without flashing a ring on pointer-open;
      // the panel is focusable (tabindex -1) and announced via aria-labelledby.
      var panel = this.querySelector(".tnd-unified-drawer__panel");
      if (panel) panel.focus({ preventScroll: true });

      if (!this._pushed) {
        history.pushState(Object.assign({}, history.state, { tndDrawer: true }), "");
        this._pushed = true;
      }
    }

    close() {
      if (!this._isOpen || this._isClosing) return;
      // With the interstitial up, "close" means step back to the cart view --
      // otherwise the history.back() below only pops the interstitial entry
      // and _isClosing would stay latched, deadlocking every dismissal.
      if (this.querySelector(".tnd-unified-drawer__interstitial")) {
        this._closeSpiritsInterstitial();
        return;
      }
      if (this._pushed && history.state && history.state.tndDrawer) {
        this._isClosing = true;
        history.back();
        return;
      }
      this._closeNow(false);
    }

    refresh() {
      var sources = window.TND && window.TND.sources;
      var tndFallback = {
        ok: false,
        count: 0,
        items: [],
        itemsSubtotalCents: 0,
        totalCents: 0,
        currency: "USD"
      };
      var spiritsFallback = { state: "ERROR", items: [], currency: "USD", count: 0 };

      // CR: each source call is guarded because the bridge can fail independently.
      var tndPromise = Promise.resolve(safeCall(sources && sources.readTnd, tndFallback));
      var spiritsPromise = sources
        ? Promise.resolve(safeCall(sources.readSpirits, spiritsFallback))
        : Promise.resolve(null);

      return Promise.all([tndPromise, spiritsPromise]).then((results) => {
        this._renderContent(results[0] || tndFallback, results[1]);
        this._prewarmSpirits(results[1]);
        this._lastRefreshAt = Date.now();
      });
    }

    _renderShell() {
      this.innerHTML =
        '<div class="tnd-unified-drawer__backdrop" data-tnd-close></div>' +
        '<section class="tnd-unified-drawer__panel" tabindex="-1">' +
        '  <header class="tnd-unified-drawer__header">' +
        '    <h2 class="tnd-unified-drawer__title" id="tnd-drawer-title">Your carts</h2>' +
        '    <button class="tnd-unified-drawer__close" type="button" aria-label="Close cart" data-tnd-close>' +
        '      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        "    </button>" +
        "  </header>" +
        '  <div class="tnd-unified-drawer__scroll">' +
        '    <div data-tnd-content></div>' +
        '    <footer class="tnd-unified-drawer__footer">Apparel and spirits are separate orders from two companies: The Nashville Distillery and BourbonHunt USA.</footer>' +
        "  </div>" +
        "</section>";

      this.addEventListener("click", (event) => {
        var qtyButton = event.target.closest("[data-tnd-qty-action]");
        if (qtyButton) {
          this._onQtyAction(qtyButton);
          return;
        }
        if (event.target.closest("[data-tnd-nudge-dismiss]")) {
          this._dismissNudge();
          return;
        }
        var copyButton = event.target.closest("[data-tnd-copy-link]");
        if (copyButton) {
          this._copyLink(copyButton);
          return;
        }
        if (event.target.closest("[data-tnd-close]")) this.close();
      });
    }

    _renderContent(tndData, spiritsData) {
      this._lastTndData = tndData || null;
      this._lastSpiritsData = spiritsData || null;

      var apparelHasItems = !!(tndData && Array.isArray(tndData.items) && tndData.items.length);
      // A repopulated apparel cart ends the post-checkout "Paid" framing.
      if (apparelHasItems) this._apparelPaid = false;
      // H-2: apparel-first is enforced, not just visual order -- when both
      // merchants have items, spirits checkout stays locked behind step 1.
      var bothStaged = apparelHasItems && this._isSpiritsCheckable(spiritsData);

      var html = this._renderWebviewNudge();
      html += this._renderTndGroup(tndData, bothStaged);
      html += this._renderSpiritsGroup(spiritsData, bothStaged);
      var content = this.querySelector("[data-tnd-content]");
      if (content) content.innerHTML = html;

      var footer = this.querySelector(".tnd-unified-drawer__footer");
      if (footer) footer.textContent = this._disclosureText(tndData, spiritsData);

      var tndCta = this.querySelector("[data-tnd-apparel-checkout]");
      if (tndCta) {
        tndCta.addEventListener("click", () => {
          if (
            this._lastSpiritsData &&
            this._lastSpiritsData.state === "OK" &&
            this._lastSpiritsData.gid
          ) {
            try {
              localStorage.setItem(
                "tnd_apparel_pending",
                JSON.stringify({
                  ts: Date.now(),
                  gid: this._lastSpiritsData.gid
                })
              );
            } catch (error) {
              // Graceful fallback for private browsing/storage restrictions.
            }
          }

          location.assign("/checkout");
        });
      }

      var spiritsRefresh = this.querySelector("[data-tnd-spirits-refresh]");
      if (spiritsRefresh) {
        spiritsRefresh.addEventListener("click", () => this.refresh());
      }

      var spiritsCta = this.querySelector("[data-tnd-spirits-checkout]");
      var spiritsAck = this.querySelector("[data-tnd-spirits-ack]");
      if (spiritsCta) {
        if (spiritsAck) {
          spiritsAck.addEventListener("change", () => {
            spiritsCta.disabled = !spiritsAck.checked;
          });
        }
        spiritsCta.addEventListener("click", () => {
          if (spiritsCta.disabled) return;
          this._openSpiritsInterstitial();
        });
      }
    }

    _isSpiritsCheckable(data) {
      if (this._isMerchSuppressed()) return false;
      var sources = window.TND && window.TND.sources;
      if (!sources || !safeCall(sources.isAgeVerified, false)) return false;
      if (!data || data.state !== "OK" || !Array.isArray(data.items) || !data.items.length) return false;
      var gid = this._resolveSpiritsGid(data);
      if (!gid) return false;
      var launch = window.TND && window.TND.launch;
      if (launch && launch.statusFor(gid) === "LAUNCHED") return false;
      return true;
    }

    _renderWebviewNudge() {
      if (this._nudgeDismissed || !inAppWebview()) return "";
      var action;
      if (isAndroid()) {
        var intentUrl =
          "intent://" + location.host + location.pathname + location.search +
          "#Intent;scheme=https;package=com.android.chrome;end";
        action = '<a class="tnd-unified-drawer__nudge-action" href="' + htmlEscape(intentUrl) + '">Open in Chrome</a>';
      } else {
        action = '<button class="tnd-unified-drawer__nudge-action" type="button" data-tnd-copy-link>Copy link</button>';
      }
      return (
        '<div class="tnd-unified-drawer__nudge" role="note">' +
        '<p>Checkout works right here &mdash; but for Apple&nbsp;Pay or Google&nbsp;Pay, open this page in your browser' +
        (isAndroid() ? "" : ", then paste the link in Safari") +
        ".</p>" +
        action +
        '<button class="tnd-unified-drawer__nudge-dismiss" type="button" data-tnd-nudge-dismiss aria-label="Dismiss">&times;</button>' +
        "</div>"
      );
    }

    _dismissNudge() {
      this._nudgeDismissed = true;
      try {
        sessionStorage.setItem(NUDGE_KEY, "1");
      } catch (error) {
        // In-memory flag still suppresses it for this page view.
      }
      var nudge = this.querySelector(".tnd-unified-drawer__nudge");
      if (nudge) nudge.remove();
    }

    _copyLink(button) {
      var url = location.href;
      var markCopied = function () {
        button.textContent = "Copied ✓";
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(markCopied, function () {});
          return;
        }
      } catch (error) {
        // Fall through to the legacy path.
      }
      try {
        var input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
        markCopied();
      } catch (error) {
        // Clipboard unavailable; the visible URL bar remains the fallback.
      }
    }

    _onQtyAction(button) {
      if (this._writeBusy || button.disabled) return;
      var sources = window.TND && window.TND.sources;
      if (!sources) return;

      var group = button.getAttribute("data-tnd-group");
      var line = button.getAttribute("data-tnd-line");
      var current = Number(button.getAttribute("data-tnd-qty-current")) || 0;
      var next = button.getAttribute("data-tnd-qty-action") === "inc" ? current + 1 : current - 1;
      if (next < 0) next = 0;
      if (!line) return;
      if (group === "tnd" && typeof sources.changeTnd !== "function") return;
      if (group === "spirits" && typeof sources.updateSpiritsLine !== "function") return;

      this._writeBusy = true;
      button.disabled = true;

      var write = group === "tnd" ? sources.changeTnd(line, next) : sources.updateSpiritsLine(line, next);
      Promise.resolve(write)
        .then((result) => {
          this._writeBusy = false;
          if (group === "tnd" && result && result.ok === true) {
            this._renderContent(result, this._lastSpiritsData);
            return undefined;
          }
          if (group === "spirits" && result && (result.state === "OK" || result.state === "EMPTY")) {
            this._renderContent(this._lastTndData, result);
            this._prewarmSpirits(result);
            return undefined;
          }
          // Write failed or came back DEGRADED -- resync both groups.
          return this.refresh();
        })
        .catch(() => {
          this._writeBusy = false;
          return this.refresh();
        });
    }

    _renderQtyStepper(group, line, qty) {
      var current = Number(qty) || 0;
      var shared =
        ' data-tnd-group="' + group + '" data-tnd-line="' + htmlEscape(line) + '" data-tnd-qty-current="' + current + '"';
      return (
        '<div class="tnd-unified-drawer__qty">' +
        '<button type="button" data-tnd-qty-action="dec"' + shared + ' aria-label="' +
        (current <= 1 ? "Remove item" : "Decrease quantity") +
        '">&minus;</button>' +
        "<span>" + htmlEscape(current) + "</span>" +
        '<button type="button" data-tnd-qty-action="inc"' + shared + ' aria-label="Increase quantity">+</button>' +
        "</div>"
      );
    }

    // CR: the disclosure must match the carts actually in play; a static
    // "charged twice" line is wrong when only one merchant has items.
    _spiritsCharged(data) {
      if (this._isMerchSuppressed()) return false;
      var sources = window.TND && window.TND.sources;
      if (!sources) return false;
      if (!safeCall(sources.isAgeVerified, false)) return false;
      if (!data || !Array.isArray(data.items) || !data.items.length) return false;
      if (data.state !== "OK" && data.state !== "DEGRADED") return false;
      var launch = window.TND && window.TND.launch;
      if (launch && launch.statusFor(this._resolveSpiritsGid(data)) === "LAUNCHED") return false;
      return true;
    }

    _disclosureText(tndData, spiritsData) {
      var apparel = !!(tndData && Array.isArray(tndData.items) && tndData.items.length);
      var spirits = this._spiritsCharged(spiritsData);
      if (apparel && spirits) {
        return "You'll see two charges: one from The Nashville Distillery for apparel, and one from BourbonHunt USA for spirits.";
      }
      if (spirits) {
        return "Your spirits charge comes from BourbonHunt USA, a licensed retailer, not from The Nashville Distillery.";
      }
      if (apparel) {
        return "Your apparel charge comes from The Nashville Distillery.";
      }
      return "Apparel and spirits are separate orders from two companies: The Nashville Distillery and BourbonHunt USA.";
    }

    _renderTndGroup(data, bothStaged) {
      var items = Array.isArray(data && data.items) ? data.items : [];
      var emptyCopy = this._apparelPaid
        ? '<p class="tnd-unified-drawer__paid">Paid &#10003; &mdash; your apparel order is complete.</p>'
        : '<p class="tnd-unified-drawer__empty">Your apparel cart is empty</p>';
      var lines = items.length ? items.map((item) => this._renderTndLine(item)).join("") : emptyCopy;
      var subtotalText = formatCents(data && data.itemsSubtotalCents);
      var ctaLabel = "Check out apparel (" + subtotalText + ")" + (bothStaged ? " — step 1 of 2" : "");
      var footer = items.length
        ? '<div class="tnd-unified-drawer__group-footer">' +
          '<div class="tnd-unified-drawer__subtotal"><span>Apparel subtotal</span><strong>' +
          htmlEscape(subtotalText) +
          "</strong></div>" +
          '<button class="tnd-unified-drawer__cta" type="button" data-tnd-apparel-checkout>' +
          htmlEscape(ctaLabel) +
          "</button>" +
          "</div>"
        : "";

      return (
        '<section class="tnd-unified-drawer__group">' +
        '<header class="tnd-unified-drawer__group-header">' +
        "<h3>The Nashville Distillery</h3>" +
        "</header>" +
        lines +
        footer +
        "</section>"
      );
    }

    _renderTndLine(item) {
      return (
        '<article class="tnd-unified-drawer__line">' +
        this._renderImage(item.image, item.title) +
        '<div class="tnd-unified-drawer__line-meta">' +
        '<a class="tnd-unified-drawer__line-title" href="' +
        htmlEscape(item.url || "#") +
        '">' +
        htmlEscape(item.title) +
        "</a>" +
        '<p class="tnd-unified-drawer__line-detail">' +
        htmlEscape(item.variantTitle || "") +
        "</p>" +
        (item.sellingPlanName
          ? '<p class="tnd-unified-drawer__line-detail">' + htmlEscape(item.sellingPlanName) + "</p>"
          : "") +
        this._renderQtyStepper("tnd", item.key, item.qty) +
        "</div>" +
        '<strong class="tnd-unified-drawer__line-price">' +
        htmlEscape(formatCents(item.finalLinePriceCents)) +
        "</strong>" +
        "</article>"
      );
    }

    _renderSpiritsTerminal(meta) {
      // Resuming the SAME checkoutUrl re-enters the same cart, and a cart
      // converts to an order exactly once (H-12) -- this link cannot
      // double-charge, unlike re-offering a fresh checkout CTA.
      var resume = meta && meta.url
        ? '<p>Didn&apos;t finish? <a class="tnd-unified-drawer__terminal-link" href="' +
          htmlEscape(meta.url) +
          '">Resume your spirits checkout</a> &mdash; reopening it won&apos;t create a second charge.</p>'
        : '<p>Didn&apos;t finish? Complete your spirits order at <a class="tnd-unified-drawer__terminal-link" href="https://bourbonhuntusa.com">bourbonhuntusa.com</a>.</p>';

      return (
        '<section class="tnd-unified-drawer__group">' +
        '<div class="tnd-unified-drawer__terminal">' +
        "<h3>BourbonHunt USA</h3>" +
        "<p>Your spirits order is being completed with BourbonHunt USA, billed &amp; shipped by a licensed retailer, not by The Nashville Distillery.</p>" +
        resume +
        "</div>" +
        "</section>"
      );
    }

    _renderSpiritsGroup(data, bothStaged) {
      if (this._isMerchSuppressed()) return "";
      var sources = window.TND && window.TND.sources;
      if (!sources) return "";
      var isAgeVerified = safeCall(sources && sources.isAgeVerified, false);
      var launch = window.TND && window.TND.launch;

      // CR-3: render no spirit item data before age verification.
      if (!isAgeVerified) {
        return (
          '<section class="tnd-unified-drawer__group">' +
          '<header class="tnd-unified-drawer__group-header">' +
          "<h3>BourbonHunt USA</h3>" +
          "<p>Sold &amp; shipped by a licensed retailer</p>" +
          "</header>" +
          '<p class="tnd-unified-drawer__age-prompt">Verify your age to view spirits</p>' +
          "</section>"
        );
      }

      if (!data || data.state === "EMPTY" || data.state === "NO_CART" || data.state === "ERROR") {
        // H-4 destroys the cart key on launch, so a just-launched cart reads
        // NO_CART here -- keep the in-progress panel up for a window.
        var last = launch && typeof launch.latestLaunch === "function" ? launch.latestLaunch() : null;
        if (last && Date.now() - last.ts < RECENT_LAUNCH_MS) {
          return this._renderSpiritsTerminal(last);
        }
        return "";
      }

      var items = Array.isArray(data.items) ? data.items : [];
      var gid = this._resolveSpiritsGid(data);
      // R1/H-4: launched GIDs are terminal and never get a second drawer checkout CTA.
      if (launch && launch.statusFor(gid) === "LAUNCHED") {
        return this._renderSpiritsTerminal(
          launch.getLaunchMeta ? launch.getLaunchMeta(gid) : null
        );
      }

      var note =
        data.state === "DEGRADED"
          ? '<p class="tnd-unified-drawer__degraded-note">Couldn\'t refresh spirits. Showing your last view' +
            htmlEscape(relativeTime(data.cachedAt)) +
            ".</p>"
          : "";
      var editable = data.state === "OK";
      var lines = items.map((item) => this._renderSpiritsLine(item, editable)).join("");
      var subtotal = data.subtotalAmount != null ? data.subtotalAmount : items.reduce((sum, item) => sum + Number(item.priceAmount || 0) * Number(item.qty || 0), 0);
      var subtotalText = formatSpiritsMoney(subtotal, data.currency || "USD");
      var canCheckout = data.state === "OK" && !!gid;
      var checkout;

      if (canCheckout && bothStaged) {
        // H-2: never two live checkout buttons -- spirits stays locked until
        // the apparel order (step 1) completes.
        checkout =
          '<button class="tnd-unified-drawer__cta" type="button" disabled>Check out spirits — step 2</button>' +
          '<p class="tnd-unified-drawer__step-note">Complete your apparel order first &mdash; spirits checkout unlocks right after.</p>';
      } else if (canCheckout) {
        var ackId = "tnd-spirits-ack-" + Math.random().toString(36).slice(2);
        var spiritsLabel = this._apparelPaid
          ? "Finish spirits order (" + subtotalText + ") — last step"
          : "Check out spirits (" + subtotalText + ")";
        checkout =
          '<label class="tnd-unified-drawer__ack" for="' +
          ackId +
          '"><input id="' +
          ackId +
          '" type="checkbox" data-tnd-spirits-ack> <span>I understand my spirits are billed and shipped by BourbonHunt USA, a licensed retailer, not by The Nashville Distillery.</span></label>' +
          '<button class="tnd-unified-drawer__cta" type="button" data-tnd-spirits-checkout disabled>' +
          htmlEscape(spiritsLabel) +
          "</button>";
      } else {
        checkout = '<button class="tnd-unified-drawer__refresh-note" type="button" data-tnd-spirits-refresh>Refresh to check out</button>';
      }

      return (
        '<section class="tnd-unified-drawer__group">' +
        '<header class="tnd-unified-drawer__group-header">' +
        "<h3>BourbonHunt USA</h3>" +
        "<p>Sold &amp; shipped by a licensed retailer</p>" +
        "</header>" +
        lines +
        note +
        '<div class="tnd-unified-drawer__group-footer">' +
        '<div class="tnd-unified-drawer__subtotal"><span>Spirits subtotal</span><strong>' +
        htmlEscape(subtotalText) +
        "</strong></div>" +
        checkout +
        "</div>" +
        "</section>"
      );
    }

    _renderSpiritsLine(item, editable) {
      var currency = item.currency || "USD";
      var qty = editable
        ? this._renderQtyStepper("spirits", item.id, item.qty)
        : '<p class="tnd-unified-drawer__line-detail">Qty ' + htmlEscape(item.qty || 0) + "</p>";
      return (
        '<article class="tnd-unified-drawer__line">' +
        this._renderImage(item.image, item.title) +
        '<div class="tnd-unified-drawer__line-meta">' +
        '<p class="tnd-unified-drawer__line-title">' +
        htmlEscape(item.title) +
        "</p>" +
        '<p class="tnd-unified-drawer__line-detail">' +
        htmlEscape(item.variantTitle || "") +
        "</p>" +
        qty +
        "</div>" +
        '<strong class="tnd-unified-drawer__line-price">' +
        htmlEscape(formatSpiritsMoney(Number(item.priceAmount || 0) * Number(item.qty || 0), currency)) +
        "</strong>" +
        "</article>"
      );
    }

    _renderImage(src, alt) {
      if (!src) return '<div class="tnd-unified-drawer__line-img" aria-hidden="true"></div>';
      return '<img class="tnd-unified-drawer__line-img" src="' + htmlEscape(src) + '" alt="' + htmlEscape(alt || "") + '" loading="lazy">';
    }

    _isMerchSuppressed() {
      return this.dataset.isMerch === "true" || location.pathname.indexOf("/collections/merch") === 0;
    }

    _resolveSpiritsGid(data) {
      var derived;

      if (data && data.gid) return data.gid;
      derived = safeCall(() => window.TND.sources && window.TND.sources.deriveSpirits && window.TND.sources.deriveSpirits(), null);
      return derived && derived.cartId ? derived.cartId : null;
    }

    _prewarmSpirits(data) {
      var launch = window.TND && window.TND.launch;
      var gid = this._resolveSpiritsGid(data);

      // CR-6: prewarm only from fresh OK source data for the same cart GID.
      if (launch && data && data.state === "OK" && data.checkoutUrl && gid) {
        launch.prewarm(data.checkoutUrl, gid);
      }
    }

    _openSpiritsInterstitial() {
      var panel = this.querySelector(".tnd-unified-drawer__panel");
      var sheet;

      if (!panel || this.querySelector(".tnd-unified-drawer__interstitial")) return;

      sheet = document.createElement("div");
      sheet.className = "tnd-unified-drawer__interstitial";
      sheet.setAttribute("role", "dialog");
      sheet.setAttribute("aria-modal", "true");
      sheet.setAttribute("tabindex", "-1");
      sheet.innerHTML =
        '<div class="tnd-unified-drawer__interstitial-sheet">' +
        "<h3>BourbonHunt USA Checkout</h3>" +
        '<ul class="tnd-unified-drawer__disclosures">' +
        "<li>You must be 21 or older.</li>" +
        "<li>Sold &amp; shipped by BourbonHunt USA, a licensed retailer.</li>" +
        "<li>Your card will be charged by BOURBONHUNT USA.<!-- /* CR-2: polished fallback; swap for exact BH/counsel statement descriptor when confirmed */ --></li>" +
        "<li>Your apparel is a separate order from The Nashville Distillery.</li>" +
        "</ul>" +
        '<p class="tnd-unified-drawer__launch-error" hidden data-tnd-launch-error></p>' +
        '<div class="tnd-unified-drawer__interstitial-actions">' +
        '<button class="tnd-unified-drawer__interstitial-primary" type="button" data-tnd-launch-continue>Continue to BourbonHunt</button>' +
        '<button class="tnd-unified-drawer__interstitial-secondary" type="button" data-tnd-launch-back>Back</button>' +
        "</div>" +
        "</div>";

      // Hide the cart view so the panel hugs the interstitial instead of
      // overlaying a full-height (often tall) cart behind it.
      var scroll = this.querySelector(".tnd-unified-drawer__scroll");
      var header = this.querySelector(".tnd-unified-drawer__header");
      if (scroll) scroll.hidden = true;
      if (header) header.hidden = true;

      panel.appendChild(sheet);
      sheet.querySelector("[data-tnd-launch-back]").addEventListener("click", () => this._closeSpiritsInterstitial());
      sheet.querySelector("[data-tnd-launch-continue]").addEventListener("click", (event) => this._launchSpirits(event.currentTarget));
      sheet.focus({ preventScroll: true });

      if (!this._interstitialPushed) {
        history.pushState(Object.assign({}, history.state, { tndSpiritsInterstitial: true }), "");
        this._interstitialPushed = true;
      }
    }

    _closeSpiritsInterstitial(fromPopstate) {
      var sheet = this.querySelector(".tnd-unified-drawer__interstitial");

      if (!sheet) return;
      sheet.remove();
      var scroll = this.querySelector(".tnd-unified-drawer__scroll");
      var header = this.querySelector(".tnd-unified-drawer__header");
      if (scroll) scroll.hidden = false;
      if (header) header.hidden = false;
      if (this._interstitialPushed) {
        this._interstitialPushed = false;
        if (!fromPopstate) {
          this._suppressInterstitialPopstate = true;
          history.back();
        }
      }
    }

    _showLaunchError(message) {
      var error = this.querySelector("[data-tnd-launch-error]");

      if (!error) return;
      error.textContent = message;
      error.hidden = false;

      // After a failed reach, the primary action is a retry, not first checkout.
      var cont = this.querySelector("[data-tnd-launch-continue]");
      if (cont) {
        cont.textContent = "Try again";
        cont.disabled = false;
      }
    }

    _launchSpirits(button) {
      var launch = window.TND && window.TND.launch;
      var gid = this._resolveSpiritsGid(this._lastSpiritsData);
      var prewarm = launch && launch.getPrewarm ? launch.getPrewarm() : null;

      if (!launch || !gid) {
        this._showLaunchError("Couldn't reach BourbonHunt. Try again.");
        return;
      }

      if (!prewarm || prewarm.gid !== gid) {
        button.textContent = "Preparing…";
        button.disabled = true;
        this._refreshSpiritsForLaunch(button, gid);
        return;
      }

      this._finishSpiritsLaunch(gid, prewarm.url);
    }

    _refreshSpiritsForLaunch(button, gid) {
      var sources = window.TND && window.TND.sources;

      if (!sources || typeof sources.readSpirits !== "function") {
        this._showLaunchError("Couldn't reach BourbonHunt. Try again.");
        button.textContent = "Try again";
        button.disabled = false;
        return;
      }

      Promise.resolve(sources.readSpirits())
        .then((data) => {
          var freshGid = this._resolveSpiritsGid(data);

          this._lastSpiritsData = data || null;
          if (!data || data.state !== "OK" || !data.checkoutUrl || !freshGid || freshGid !== gid) {
            this._showLaunchError("Couldn't reach BourbonHunt. Try again.");
            button.textContent = "Try again";
            button.disabled = false;
            return;
          }

          window.TND.launch.prewarm(data.checkoutUrl, freshGid);
          this._finishSpiritsLaunch(freshGid, data.checkoutUrl);
        })
        .catch(() => {
          this._showLaunchError("Couldn't reach BourbonHunt. Try again.");
          button.textContent = "Try again";
          button.disabled = false;
        });
    }

    _finishSpiritsLaunch(gid, url) {
      var ok = window.TND.launch.markLaunched(gid, url);

      // Condition 2: never navigate unless at least one durable launched layer persists.
      if (ok === false) {
        this._showLaunchError("Your browser is blocking storage. Open in your default browser to check out spirits.");
        return;
      }

      // H-4: destroy the SDK's cart key, don't just flag -- future adds get a
      // fresh cart instead of disappearing into this terminal one.
      var sources = window.TND && window.TND.sources;
      if (sources && typeof sources.destroyCartKeys === "function") {
        try {
          sources.destroyCartKeys(gid);
        } catch (error) {
          // The launched flag alone still blocks any re-offer.
        }
      }

      window.location.assign(url);
    }

    _onPopstate() {
      if (this._suppressInterstitialPopstate) {
        this._suppressInterstitialPopstate = false;
        return;
      }
      if (this.querySelector(".tnd-unified-drawer__interstitial")) {
        this._isClosing = false;
        this._closeSpiritsInterstitial(true);
        return;
      }
      if (this._isOpen) this._closeNow(true);
    }

    _closeNow() {
      this._isOpen = false;
      this._isClosing = false;
      this._pushed = false;
      this._interstitialPushed = false;
      this._closeSpiritsInterstitial(true);
      this.removeAttribute("data-open");
      document.documentElement.classList.remove(OPEN_CLASS);
      document.removeEventListener("keydown", this._boundKeydown);
      setTimeout(() => {
        if (!this._isOpen) this.hidden = true;
      }, TRANSITION_MS);
      if (this._lastFocus && typeof this._lastFocus.focus === "function") {
        this._lastFocus.focus({ preventScroll: true });
      }
    }

    _onKeydown(event) {
      if (!this._isOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (this.querySelector(".tnd-unified-drawer__interstitial")) {
          this._closeSpiritsInterstitial();
          return;
        }
        this.close();
        return;
      }
      if (event.key !== "Tab") return;

      var panel = this.querySelector(".tnd-unified-drawer__interstitial") || this.querySelector(".tnd-unified-drawer__panel");
      if (!panel) return;
      var focusable = Array.prototype.slice.call(
        panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _onRefreshTrigger() {
      if (!this._isOpen) return;
      if (document.visibilityState && document.visibilityState !== "visible") return;
      var elapsed = Date.now() - this._lastRefreshAt;
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this.refresh(), Math.max(REFRESH_DEBOUNCE_MS - elapsed, 0));
    }

    _onAgeVerified() {
      if (!this._isOpen) return;
      this.refresh();
    }
  }

  if (!customElements.get(DRAWER_TAG)) {
    customElements.define(DRAWER_TAG, TndUnifiedDrawer);
  }

  window.TND.drawer = {
    open: function (trigger) {
      var drawer = getDrawer();
      if (drawer) drawer.open(trigger);
    },
    close: function () {
      var drawer = getDrawer();
      if (drawer) drawer.close();
    },
    refresh: function () {
      var drawer = getDrawer();
      return drawer ? drawer.refresh() : Promise.resolve();
    },
    // Set by the launchpad right before its post-checkout reconvene open;
    // flips the empty apparel group to "Paid" and spirits to "last step".
    markApparelPaid: function () {
      var drawer = getDrawer();
      if (drawer) drawer._apparelPaid = true;
    },
    _openSpiritsInterstitial: function () {
      var drawer = getDrawer();
      if (drawer) drawer._openSpiritsInterstitial();
    }
  };

  function shouldBindInterception() {
    var drawer = getDrawer();
    return drawer && drawer.dataset.themeEditor !== "true";
  }

  function closeNativeCartDrawer() {
    var nativeDrawer = document.querySelector('cart-drawer#cart-drawer');
    if (!nativeDrawer) return;
    if (typeof nativeDrawer.hide === "function") {
      nativeDrawer.hide();
      return;
    }
    nativeDrawer.removeAttribute("open");
    nativeDrawer.setAttribute("hidden", "");
    // CR: do not remove html.lock here; this drawer never owns Prestige or age-gate locks.
  }

  document.addEventListener(
    "click",
    function (event) {
      if (!shouldBindInterception()) return;
      var trigger = event.target.closest('[aria-controls="cart-drawer"]');
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.TND.drawer.open(trigger);
    },
    true
  );

  function neutralizeNativeAutoOpen(event) {
    if (!shouldBindInterception()) return;
    if (event.type === "cart:change" && (!event.detail || event.detail.baseEvent !== "variant:add")) return;
    requestAnimationFrame(function () {
      closeNativeCartDrawer();
      window.TND.drawer.open();
    });
  }

  // Prestige dispatches variant:add from the product form, then cart:change on documentElement;
  // CartDrawer listens to cart:change on document and opens when baseEvent is variant:add.
  document.addEventListener("variant:add", neutralizeNativeAutoOpen, true);
  document.addEventListener("cart:change", neutralizeNativeAutoOpen, true);
})();
